/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { NodeConfig } from '@/client';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Select } from '@/primitives/Select';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type ParentConfigSelectorProps = {
  disabled?: boolean;
  onConfigSelect: (config: NodeConfig | null) => void;
};

export function ParentConfigSelector({ disabled, onConfigSelect }: ParentConfigSelectorProps) {
  const { t } = useMimicTranslation('nodeConfigurations');
  const selectedSubtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  // Query for existing configs filtered by current subtenant/tenant
  const configsQuery = usePaginatedNodeConfigsQuery({
    params: {
      pageSize: 100,
      filters: {
        ...(searchValue ? { name: searchValue } : {}),
        ...(selectedSubtenantID ? { subtenantId: selectedSubtenantID } : {}),
      },
    },
    type: 'inMemory',
  });

  const debouncedSearch = useMemo(() => debounce((value: string) => setSearchValue(value), 300), []);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const handleChange = useCallback(
    (configId: string | undefined) => {
      if (configId) {
        const selectedConfig = configsQuery.data?.data.find((c) => c.id === configId);
        if (selectedConfig) {
          onConfigSelect(selectedConfig);
        }
      } else {
        onConfigSelect(null);
      }
      // Clear search when selection changes
      setSearchInput('');
      setSearchValue('');
    },
    [configsQuery.data?.data, onConfigSelect],
  );

  const configOptions =
    configsQuery.data?.data.map((config) => ({
      label: `${config.name} (${config.os})`,
      value: config.id,
    })) || [];

  return (
    <Form.Item
      label={t('upload.form.parentConfiguration')}
      name="parentConfigId"
      help={t('upload.form.parentConfigurationHelp')}
    >
      <Select
        placeholder={t('upload.form.parentConfigurationPlaceholder')}
        options={configOptions}
        allowClear
        showSearch
        searchValue={searchInput}
        onSearch={handleSearch}
        onChange={handleChange}
        filterOption={false}
        disabled={disabled}
        data-testid="parent-config-selector"
        notFoundContent={configsQuery.isLoading ? t('upload.form.loading') : t('upload.form.noConfigurationsFound')}
      />
    </Form.Item>
  );
}
